// store/slices/userSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { userApi } from "../../services/userApi";
import { fetchAppConfig } from "../../services/configApi";
import type { UserState } from "../types/userTypes";

const initialState: UserState = {
  user: null,
  isGuest: false,
  authEnabled: true, // Default to true until we know otherwise
  guestAccessEnabled: true, // Default to true until we know otherwise
  appFeedbackEnabled: true, // Default to true until we know otherwise
  chatFeedbackEnabled: true, // Default to true until we know otherwise
  chatHistoryEnabled: true, // Default to true until we know otherwise
  streamingEnabled: false, // Default to false — enabled per-channel config
  domainRouting: null,
  domainConfig: null,
  roles: [],
  loading: true, // Start as loading until initializeUser completes
  error: null,
};

/**
 * Initialize app — fetch config and attempt to resolve an existing user.
 *
 * For guests: the backend no longer auto-creates a user on /api/users/me.
 * If the guest has no user record yet, /api/users/me returns 401 and we
 * proceed with user=null. The user will be created on first chat message.
 */
export const initializeUser = createAsyncThunk(
  "user/initialize",
  async (_, { rejectWithValue }) => {
    try {
      // Always fetch config; try to resolve user (may not exist yet for new guests)
      const [userResult, config] = await Promise.all([
        userApi.fetchCurrentUser().catch(() => null),
        fetchAppConfig(),
      ]);

      return { user: userResult, config };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    logout: () => initialState,
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize user
      .addCase(initializeUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.authEnabled = action.payload.config.authEnabled;
        state.guestAccessEnabled = action.payload.config.guestAccessEnabled;
        state.appFeedbackEnabled = action.payload.config.appFeedbackEnabled;
        state.chatFeedbackEnabled = action.payload.config.chatFeedbackEnabled;
        state.chatHistoryEnabled = action.payload.config.chatHistoryEnabled;
        state.streamingEnabled = action.payload.config.streamingEnabled ?? false;
        state.domainRouting = action.payload.config.domainRouting ?? null;
        state.domainConfig = action.payload.config.domainConfig ?? null;
        state.isGuest = !action.payload.user || action.payload.user.authType === "guest";
        state.roles = action.payload.user?.roles ?? ["user"];
      })
      .addCase(initializeUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearError } = userSlice.actions;

export default userSlice.reducer;
